import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Phone, ShieldCheck } from "lucide-react";
import { Breadcrumbs, type Crumb } from "@/components/site/Breadcrumbs";
import { HeroBackground } from "@/components/site/HeroBackground";
import { brand, founder } from "@/data/site/brand";
import { stockImages, type StockImage, type StockImageKey } from "@/data/site/stockImages";

interface ResourceHeroProps {
  breadcrumbs: Crumb[];
  eyebrow: string;
  title: ReactNode;
  /** One-sentence answer/summary shown above the fold (great for AEO). */
  summary: ReactNode;
  /** Optional badges/pills rendered inline next to the title (e.g. urgency, deadline). */
  meta?: ReactNode;
  /** Optional quick-fact list rendered as a compact key/value row (great for featured snippets). */
  facts?: { label: string; value: ReactNode }[];
  /** Optional right-rail content (e.g. jump links, related). */
  aside?: ReactNode;
  /**
   * Optional background image (visual accent). Accepts a stock image key or a
   * full StockImage object. Rendered behind a heavy navy gradient so text and
   * buttons remain fully readable. Defaults to `taxFormsCalculator`.
   */
  background?: StockImageKey | StockImage | false;
}

export const ResourceHero = ({
  breadcrumbs,
  eyebrow,
  title,
  summary,
  meta,
  facts,
  aside,
  background = "taxFormsCalculator",
}: ResourceHeroProps) => {
  const bgImage =
    background === false
      ? null
      : typeof background === "string"
        ? stockImages[background]
        : background;
  return (
  <section className="relative bg-navy-dark text-white border-b border-white/10 overflow-hidden">
    {bgImage && <HeroBackground image={bgImage} priority />}
    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 max-w-6xl">
      <Breadcrumbs items={breadcrumbs} tone="dark" />
      <div className="mt-3 grid lg:grid-cols-[1fr_auto] gap-x-12 gap-y-6 items-start">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
            {eyebrow}
          </p>
          <h1 className="font-heading text-3xl lg:text-[2.75rem] font-bold leading-[1.1] [text-wrap:balance]">
            {title}
          </h1>
          {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
          <p className="mt-4 text-base lg:text-lg text-white/85 max-w-2xl leading-relaxed [text-wrap:pretty]">
            {summary}
          </p>

          {facts && facts.length > 0 && (
            <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 max-w-2xl">
              {facts.map((f) => (
                <div key={f.label} className="border-l-2 border-gold/70 pl-3">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/55">
                    {f.label}
                  </dt>
                  <dd className="text-sm font-semibold text-white mt-0.5">{f.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* Trust + CTA strip */}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/75">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-gold" />
              Reviewed by {founder.name}, {founder.credentials}
            </span>
            <span className="hidden sm:inline text-white/25">•</span>
            <Link to="/about/franklin-sofi" className="underline-offset-2 hover:text-gold hover:underline">
              About the author
            </Link>
            <span className="hidden sm:inline text-white/25">•</span>
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center gap-1.5 font-semibold text-gold hover:text-gold-light"
            >
              <Phone className="w-3.5 h-3.5" /> {brand.phone}
            </a>
          </div>
        </div>

        {aside && (
          <div className="lg:max-w-xs lg:min-w-[240px] lg:border-l lg:border-white/10 lg:pl-8">
            {aside}
          </div>
        )}
      </div>
    </div>
  </section>
  );
};

